import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { LANGUAGE_PAGES } from "@/lib/languagePages";
import { useLanguagePageTutorPhotos, useLanguagePageTutors, useMounted } from "@/lib/languagePageTutors";
import { formatUsdWhole } from "@/lib/money";

// Up to three tutors a visitor can book in the page's language (#1644), each linking to their public page
// (/tutors/<slug>), which a search engine can read: the catalog card links into the signed-in app. The
// server chooses them (bookable, a public page, newest first) and the list can be empty: then nothing is
// drawn, since a "no tutors yet" block on a page meant to rank reads as a soft 404. The photos come after
// mount (their links expire); the initials show first.
const LanguageTutors = ({ slug }: { slug: string }) => {
  const { t, i18n } = useTranslation();
  const code = LANGUAGE_PAGES.find((entry) => entry.slug === slug)?.code;
  const { data: tutors } = useLanguagePageTutors(code);
  const photos = useLanguagePageTutorPhotos(code, useMounted());

  const visible = (tutors ?? []).filter((tutor) => tutor.slug);
  if (visible.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-20 max-w-5xl" data-testid="language-tutors">
      <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-10 text-center">
        {t("languagePageTutors.heading")}
      </h2>
      <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {visible.map((tutor) => {
          const photo = photos.get(tutor.id);
          const hasRating = tutor.averageRating != null && tutor.reviewCount > 0;
          return (
            <li key={tutor.id}>
              <Link
                to={`/tutors/${tutor.slug}`}
                className="focus-ring block h-full rounded-xl"
                data-testid="language-tutor-card"
              >
                <Card className="h-full border-border shadow-sm transition-shadow hover:shadow-md">
                  <CardContent className="p-6 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-14 w-14">
                        {photo ? <AvatarImage src={photo} alt="" /> : null}
                        <AvatarFallback>{tutor.user.initials}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-foreground truncate">{tutor.user.fullName}</h3>
                        <p className="text-base font-bold text-foreground leading-tight">
                          {formatUsdWhole(tutor.hourlyRateCents, i18n.language)}
                          <span className="text-xs font-normal text-muted-foreground">{t("tutorProfilePage.perClass")}</span>
                        </p>
                      </div>
                    </div>
                    {tutor.profileTitle ? (
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{tutor.profileTitle}</p>
                    ) : null}
                    {hasRating ? (
                      <p className="flex items-center gap-1 text-sm text-foreground">
                        <Star aria-hidden="true" className="w-4 h-4 fill-primary text-primary" />
                        <span className="font-semibold">{tutor.averageRating!.toFixed(1)}</span>
                        <span className="text-muted-foreground">{t("tutorProfilePage.reviewCount", { count: tutor.reviewCount })}</span>
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              </Link>
            </li>
          );
        })}
      </ul>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        <Trans i18nKey="languagePageTutors.order" components={{ order: <Link to="/how-we-order-tutors" className="underline" /> }} />
      </p>
    </section>
  );
};

export default LanguageTutors;
